import { useState, useEffect } from "react";
import { toast } from "sonner";
import { g, p, errMsg } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MessageSquarePlus, Star, Send, CheckCircle2 } from "lucide-react";

const Stelle = ({ value, onChange, testid }) => (
  <div className="mt-1.5 flex gap-1" data-testid={testid}>
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        data-testid={`${testid}-${n}`}
        onClick={() => onChange(n)}
        className="transition-transform hover:scale-110 active:scale-95"
        aria-label={`${n}`}
      >
        <Star className={`h-7 w-7 ${n <= value ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
      </button>
    ))}
  </div>
);

/** Questionario di gradimento della versione beta: raccoglie voto, area preferita e consigli. */
export const Questionario = ({ open, onOpenChange, business = "", role = "" }) => {
  const { t, lang } = useI18n();
  const [areas, setAreas] = useState([]);
  const [sent, setSent] = useState(false);
  const [f, setF] = useState({ rating: 0, ease: 0, best_area: "", missing: "", would_use: "", email: "" });

  useEffect(() => {
    if (open) {
      setSent(false);
      g("/feedback/areas").then((d) => setAreas(d.areas)).catch(() => setAreas([]));
    }
  }, [open]);

  const submit = async () => {
    if (!f.rating) return toast.error(t("fb.needRating"));
    try {
      await p("/feedback", { ...f, business, role, lang });
      setSent(true);
      toast.success(t("fb.thanks"));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-lg" data-testid="feedback-dialog">
        <DialogHeader>
          <DialogTitle>{t("fb.title")}</DialogTitle>
        </DialogHeader>

        {sent ? (
          <div className="py-6 text-center" data-testid="feedback-sent">
            <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-500" />
            <p className="mt-3 text-lg font-semibold">{t("fb.thanks")}</p>
            <p className="mt-1 text-sm text-slate-500">{t("fb.thanksSub")}</p>
            <Button className="mt-5 h-11 w-full" onClick={() => onOpenChange(false)} data-testid="feedback-close-btn">
              {t("fb.close")}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="rounded-md bg-amber-50 p-3 text-xs text-amber-900">{t("fb.intro")}</p>

            <div>
              <Label className="text-xs">{t("fb.q1")}</Label>
              <Stelle value={f.rating} onChange={(n) => setF({ ...f, rating: n })} testid="fb-rating" />
            </div>

            <div>
              <Label className="text-xs">{t("fb.q2")}</Label>
              <Stelle value={f.ease} onChange={(n) => setF({ ...f, ease: n })} testid="fb-ease" />
            </div>

            <div>
              <Label className="text-xs">{t("fb.q3")}</Label>
              <div className="mt-1.5 flex flex-wrap gap-2">
                {areas.map((a) => (
                  <button
                    key={a}
                    data-testid={`fb-area-${a.split(" ")[0].toLowerCase()}`}
                    onClick={() => setF({ ...f, best_area: a })}
                    className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                      f.best_area === a ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}
                  >
                    {a}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-xs">{t("fb.q4")}</Label>
              <textarea
                className="mt-1.5 min-h-[90px] w-full rounded-md border border-slate-200 p-3 text-sm outline-none focus:border-slate-400"
                placeholder={t("fb.q4ph")}
                value={f.missing}
                onChange={(e) => setF({ ...f, missing: e.target.value })}
                data-testid="fb-missing-input"
              />
            </div>

            <div>
              <Label className="text-xs">{t("fb.q5")}</Label>
              <div className="mt-1.5 flex flex-wrap gap-2">
                {[["si", t("fb.yes")], ["forse", t("fb.maybe")], ["no", t("fb.no")]].map(([id, lab]) => (
                  <button
                    key={id}
                    data-testid={`fb-use-${id}`}
                    onClick={() => setF({ ...f, would_use: id })}
                    className={`rounded-md px-4 py-2 text-sm font-semibold transition-colors ${
                      f.would_use === id ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}
                  >
                    {lab}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-xs">{t("fb.q6")}</Label>
              <Input className="mt-1.5 h-11" type="email" placeholder="nome@locale.it" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="fb-email-input" />
              <p className="mt-1 text-[11px] text-slate-500">{t("fb.q6note")}</p>
            </div>

            <Button className="h-12 w-full" onClick={submit} data-testid="feedback-submit-btn">
              <Send className="mr-1.5 h-4 w-4" /> {t("fb.send")}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

/** Pulsante flottante sempre raggiungibile durante la prova. */
export const FeedbackButton = ({ business, role }) => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        data-testid="feedback-fab"
        onClick={() => setOpen(true)}
        title={t("fb.title")}
        className="fixed bottom-[76px] right-4 z-40 flex items-center gap-2 rounded-full bg-slate-900 px-4 py-3 text-sm font-semibold text-white shadow-xl transition-transform hover:scale-105 active:scale-95 lg:bottom-5 lg:right-5"
      >
        <MessageSquarePlus className="h-4 w-4" />
        <span className="hidden sm:inline">{t("fb.fab")}</span>
      </button>
      <Questionario open={open} onOpenChange={setOpen} business={business} role={role} />
    </>
  );
};
